import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Sparkles, Plus, Trash2, Send, Calendar as CalendarIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api, ApiError } from '@/lib/api'
import { charCount, toEpochSeconds, toLocalDatetimeInput } from '@/lib/utils'
import { useAuth } from '@/lib/auth-context'

export default function Threads() {
  const { me } = useAuth()
  const navigate = useNavigate()
  const [input, setInput] = useState('')
  const [target, setTarget] = useState<number | ''>('')
  const [generating, setGenerating] = useState(false)
  const [tweets, setTweets] = useState<string[]>([])

  const [fireAt, setFireAt] = useState(
    toLocalDatetimeInput(Math.floor(Date.now() / 1000) + 30 * 60),
  )
  const [saving, setSaving] = useState(false)

  async function generate() {
    setGenerating(true)
    try {
      const res = await api.generateThread({
        input,
        targetTweetCount: typeof target === 'number' ? target : undefined,
      })
      setTweets(res.tweets)
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Generation failed')
    } finally {
      setGenerating(false)
    }
  }

  function updateTweet(idx: number, value: string) {
    setTweets((prev) => prev.map((t, i) => (i === idx ? value : t)))
  }

  function removeTweet(idx: number) {
    setTweets((prev) => prev.filter((_, i) => i !== idx))
  }

  function addTweet() {
    setTweets((prev) => [...prev, ''])
  }

  async function schedule() {
    if (tweets.some((t) => !t.trim())) {
      toast.error('Remove empty tweets before scheduling')
      return
    }
    setSaving(true)
    try {
      await api.createScheduled({
        kind: tweets.length === 1 ? 'tweet' : 'thread',
        tweets,
        fireAt: toEpochSeconds(fireAt),
      })
      toast.success('Scheduled')
      navigate('/app/schedule')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not schedule')
    } finally {
      setSaving(false)
    }
  }

  async function sendNow() {
    if (!me?.x.connected) {
      toast.error('Connect your X account first (Settings → Connect X)')
      return
    }
    if (tweets.some((t) => !t.trim())) {
      toast.error('Remove empty tweets before sending')
      return
    }
    setSaving(true)
    try {
      await api.createScheduled({
        kind: tweets.length === 1 ? 'tweet' : 'thread',
        tweets,
        fireAt: Math.floor(Date.now() / 1000),
      })
      toast.success('Queued — will fire on the next cron tick (within 5 min)')
      navigate('/app/schedule')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not send')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Threads</h1>
        <p className="text-muted-foreground">
          Paste a changelog, a rough note, or a raw idea. Claude polishes it into a thread in your voice.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Source material</CardTitle>
          <CardDescription>Anything goes — bullets, paragraphs, a release note.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            rows={8}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="We shipped X. It solves Y. The tricky part was Z…"
          />
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="target" className="text-xs">Target tweet count</Label>
              <Input
                id="target"
                type="number"
                min={1}
                max={20}
                placeholder="auto"
                value={target === '' ? '' : target}
                onChange={(e) => setTarget(e.target.value === '' ? '' : Math.max(1, Math.min(20, Number(e.target.value))))}
                className="w-28"
              />
            </div>
            <Button onClick={generate} disabled={generating || !input.trim()}>
              <Sparkles className="h-4 w-4" />
              {generating ? 'Writing…' : 'Generate thread'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {tweets.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Draft</CardTitle>
            <CardDescription>Edit freely. Each tweet has its own 280-char limit.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {tweets.map((t, i) => (
              <div key={i} className="rounded-md border bg-background p-3">
                <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                  <span>#{i + 1}</span>
                  <div className="flex items-center gap-2">
                    <span className={charCount(t) > 280 ? 'text-destructive' : ''}>{charCount(t)}/280</span>
                    <button onClick={() => removeTweet(i)} className="hover:text-destructive">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <Textarea rows={3} value={t} onChange={(e) => updateTweet(i, e.target.value)} />
              </div>
            ))}
            <Button variant="ghost" size="sm" onClick={addTweet}>
              <Plus className="h-4 w-4" />
              Add tweet
            </Button>

            <div className="flex flex-wrap items-end gap-3 border-t pt-4">
              <div className="space-y-1">
                <Label htmlFor="fireAt" className="text-xs">Schedule for</Label>
                <Input
                  id="fireAt"
                  type="datetime-local"
                  value={fireAt}
                  onChange={(e) => setFireAt(e.target.value)}
                  className="w-56"
                />
              </div>
              <Button onClick={schedule} disabled={saving} variant="secondary">
                <CalendarIcon className="h-4 w-4" />
                Schedule
              </Button>
              <Button onClick={sendNow} disabled={saving}>
                <Send className="h-4 w-4" />
                Send now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
